import React, { useState, useEffect, useRef, useCallback, useImperativeHandle } from 'react';
import * as protobuf from 'protobufjs';
import { Buffer } from 'buffer';
import CookieConsent from './components/CookieConsent';
import PrivacyPage from './components/PrivacyPage';
import TermsPage from './components/TermsPage';
import AboutPage from './components/AboutPage';
import Footer from './components/Footer';

// --- CONFIGURATION ---
const ADSENSE_CLIENT_ID = "ca-pub-3880394864354421";
const ADSENSE_SLOT_SIDEBAR = "3021389352";
const ADSENSE_SLOT_FOOTER = "2227182431";

// --- TYPES ---
export interface LineNumberEditorHandle {
  toggleSearch: () => void;
}

// --- HELPERS ---
const bufferToHex = (buffer: Uint8Array) => {
  return Array.from(buffer)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join(' ');
};

const hexToBuffer = (hex: string) => {
  const cleanHex = hex.replace(/\s+/g, '').replace(/0x/g, '');
  const pairs = cleanHex.match(/[\da-f]{2}/gi);
  if (!pairs) return new Uint8Array();
  return new Uint8Array(pairs.map((h) => parseInt(h, 16)));
};

const generateTemplate = (type: protobuf.Type): any => {
  const obj: any = {};
  for (const field of type.fieldsArray) {
    if (field.repeated) {
      obj[field.name] = [];
    } else if (field.map) {
      obj[field.name] = {};
    } else if (field.resolvedType instanceof protobuf.Type) {
        if (field.resolvedType !== type) {
            obj[field.name] = generateTemplate(field.resolvedType);
        } else {
            obj[field.name] = {}; 
        }
    } else {
      switch (field.type) {
        case 'string': obj[field.name] = field.defaultValue || ""; break;
        case 'bool': obj[field.name] = field.defaultValue || false; break;
        case 'int32': case 'uint32': case 'int64': case 'uint64': 
        case 'float': case 'double': obj[field.name] = field.defaultValue || 0; break;
        case 'bytes': obj[field.name] = "base64_string"; break;
        default: obj[field.name] = null;
      }
    }
  }
  return obj;
};

// --- ICONS ---

const SearchIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
);
const ReplaceIcon = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="m3 7 3-3 3 3" /><path d="M6 4v10c0 4 2 5 9 5" /><path d="m21 17-3 3-3-3" /><path d="M18 20V10c0-4-2-5-9-5" /></svg>
);
const XIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

const ChevronDown = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="m6 9 6 6 6-6" /></svg>
);
const ArrowRightLeft = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="m16 3 4 4-4 4"/><path d="M20 7H4"/><path d="m8 21-4-4 4-4"/><path d="M4 17h16"/></svg>
);
const CopyIcon = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><rect width="14" height="14" x="8" y="8" rx="2" ry="2" /><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" /></svg>
);
const CheckCircle = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
);
const FileCode = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/></svg>
);
const FileDigit = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><rect width="14" height="14" x="8" y="8" rx="2" ry="2" /><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" /><path d="M10 12h.01" /><path d="M14 12h.01" /></svg>
);
const AlertTriangle = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="m21.73 18-8-14a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" /><path d="M12 9v4" /><path d="M12 17h.01" /></svg>
);
const Database = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>
);
const SunIcon = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><circle cx="12" cy="12" r="4" /><path d="M12 2v2" /><path d="M12 20v2" /><path d="m4.93 4.93 1.41 1.41" /><path d="m17.66 17.66 1.41 1.41" /><path d="M2 12h2" /><path d="M20 12h2" /><path d="m6.34 17.66-1.41 1.41" /><path d="m19.07 4.93-1.41 1.41" /></svg>
);
const MoonIcon = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" /></svg>
);
const TrashIcon = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" /></svg>
);
const RefreshCw = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" /><path d="M8 16H3v5" /></svg>
);
const MenuIcon = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><line x1="4" x2="20" y1="12" y2="12"/><line x1="4" x2="20" y1="6" y2="6"/><line x1="4" x2="20" y1="18" y2="18"/></svg>
);

// --- COMPONENTS ---

const GoogleAd: React.FC<{ client: string; slot: string; format?: string; className?: string; style?: any }> = ({ client, slot, format = 'auto', className, style }) => {
  const adRef = useRef<HTMLModElement>(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    try {
      if (!adRef.current) {
        return;
      }

      // Check if ad has already been initialized by AdSense
      const status = adRef.current.getAttribute('data-adsbygoogle-status');
      if (status === 'done' || status === 'filled') {
        initializedRef.current = true;
        return;
      }

      // If already initialized in this component instance, don't re-initialize
      if (initializedRef.current) {
        return;
      }

      // Check if this specific slot already exists in the DOM (excluding current element)
      const allAds = document.querySelectorAll(`ins[data-ad-slot="${slot}"]`);
      const otherAds = Array.from(allAds).filter(ad => ad !== adRef.current);
      if (otherAds.length > 0) {
        // Another ad with same slot exists, don't initialize
        return;
      }

      // Initialize the ad
      (window as any).adsbygoogle = (window as any).adsbygoogle || [];
      (window as any).adsbygoogle.push({});
      initializedRef.current = true;
    } catch (e) {
      // Silently handle errors to avoid console spam
      if (process.env.NODE_ENV === 'development') {
        console.error("AdSense Error", e);
      }
    }
  }, [slot]);

  return (
    <div className={className} style={style}>
      <ins
        ref={adRef}
        className="adsbygoogle"
        style={{ display: 'block', width: '100%', height: '100%' }}
        data-ad-client={client}
        data-ad-slot={slot}
        data-ad-format={format}
        data-ad-full-width-responsive="true"
      ></ins>
    </div>
  );
};

const IconButton: React.FC<{ onClick?: () => void; icon: React.ReactNode; label?: string; tooltip: string; theme: any }> = ({ onClick, icon, label, tooltip, theme }) => (
    <button 
        onClick={onClick} 
        title={tooltip}
        className={`flex items-center gap-1.5 px-2 py-1 text-xs rounded border transition-colors ${theme.inactiveNav} border-transparent hover:border-gray-300 dark:hover:border-gray-600`}
    >
        {icon}
        {label && <span>{label}</span>}
    </button>
);

const LineNumberEditor = React.forwardRef<LineNumberEditorHandle, any>(
  ({ value, onChange, theme, placeholder, readOnly, className, hideSearch, onSearchChange }, ref) => {
    // Refs
    const linesRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    
    // Search State
    const [showSearch, setShowSearch] = useState(false);
    const [showReplace, setShowReplace] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [replaceText, setReplaceText] = useState('');
    const [matchCount, setMatchCount] = useState({ current: 0, total: 0 });

    // Expose Toggle Method via Ref
    useImperativeHandle(ref, () => ({
        toggleSearch: () => {
             if (hideSearch) return;
             setShowSearch(prev => {
                 const next = !prev;
                 if (next) setTimeout(() => searchInputRef.current?.focus(), 50);
                 return next;
             });
        }
    }));

    // Sync state to parent
    useEffect(() => {
        if(onSearchChange) onSearchChange(showSearch);
    }, [showSearch, onSearchChange]);

    // Force close if hideSearch prop becomes true
    useEffect(() => {
        if(hideSearch && showSearch) setShowSearch(false);
    }, [hideSearch, showSearch]);


    // --- Search Logic ---
    const findNext = useCallback(() => {
        if (!searchText || !textareaRef.current) return;
        
        const text = textareaRef.current.value;
        const currentPos = textareaRef.current.selectionEnd || 0;
        
        let index = text.toLowerCase().indexOf(searchText.toLowerCase(), currentPos);
        if (index === -1) index = text.toLowerCase().indexOf(searchText.toLowerCase(), 0);

        if (index !== -1) {
            textareaRef.current.focus();
            textareaRef.current.setSelectionRange(index, index + searchText.length);
            const matches = text.match(new RegExp(searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'));
            setMatchCount({ current: index, total: matches ? matches.length : 0 });
        }
    }, [searchText]);

    const handleReplace = () => {
        if (readOnly || !textareaRef.current) return;
        const textarea = textareaRef.current;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selectedText = textarea.value.substring(start, end);

        if (selectedText.toLowerCase() === searchText.toLowerCase()) {
            const newValue = textarea.value.substring(0, start) + replaceText + textarea.value.substring(end);
            const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, "value")?.set;
            nativeInputValueSetter?.call(textarea, newValue);
            const event = new Event('input', { bubbles: true });
            textarea.dispatchEvent(event);
            onChange({ target: { value: newValue } });
            setTimeout(findNext, 0);
        } else {
            findNext();
        }
    };

    const handleReplaceAll = () => {
        if (readOnly || !textareaRef.current) return;
        if (!searchText) return;
        const newValue = value.replaceAll(searchText, replaceText);
        onChange({ target: { value: newValue } });
    };

    // --- Event Handlers ---
    const handleScroll = () => {
      if (linesRef.current && textareaRef.current) linesRef.current.scrollTop = textareaRef.current.scrollTop;
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
            if (hideSearch) return;
            e.preventDefault();
            setShowSearch(true);
            setTimeout(() => searchInputRef.current?.focus(), 50);
        }
    };

    const lineCount = value.split('\n').length;

    return (
      <div className={`relative flex flex-col w-full h-full ${className}`}>
        
        {/* Search Toolbar */}
        {showSearch && !hideSearch && (
            <div className={`flex flex-col gap-2 p-2 border-b animate-in slide-in-from-top-2 duration-200 ${theme.panelBg} ${theme.border}`}>
                <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                        <SearchIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 opacity-50" />
                        <input 
                            ref={searchInputRef}
                            type="text"
                            placeholder="Find..." 
                            className={`w-full pl-8 pr-2 py-1 text-xs rounded border focus:ring-1 focus:ring-blue-500 outline-none ${theme.inputBorder} ${theme.bg} ${theme.text}`}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            onKeyDown={(e) => { 
                                if(e.key === 'Enter') { e.preventDefault(); findNext(); } 
                                else if (e.key === 'Escape') { setShowSearch(false); textareaRef.current?.focus(); }
                            }}
                        />
                        {matchCount.total > 0 && (
                             <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] opacity-50">{matchCount.total} matches</span>
                        )}
                    </div>
                    <button onClick={findNext} className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${theme.text}`} title="Find Next">
                        <ChevronDown className="w-4 h-4" /> 
                    </button>
                    {!readOnly && (
                        <button 
                            onClick={() => setShowReplace(!showReplace)}
                            className={`p-1.5 rounded transition-colors ${showReplace ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-100' : 'hover:bg-gray-200 dark:hover:bg-gray-700'}`}
                            title="Toggle Replace"
                        >
                            <ReplaceIcon className="w-4 h-4" />
                        </button>
                    )}
                    <button 
                        onClick={() => { setShowSearch(false); setSearchText(''); textareaRef.current?.focus(); }}
                        className="p-1.5 rounded hover:bg-red-100 hover:text-red-500"
                    >
                        <XIcon className="w-4 h-4" />
                    </button>
                </div>
                {showReplace && !readOnly && (
                    <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                            <input 
                                type="text"
                                placeholder="Replace with..." 
                                className={`w-full px-2 py-1 text-xs rounded border focus:ring-1 focus:ring-blue-500 outline-none ${theme.inputBorder} ${theme.bg} ${theme.text}`}
                                value={replaceText}
                                onChange={(e) => setReplaceText(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleReplace()}
                            />
                        </div>
                        <button onClick={handleReplace} className={`px-2 py-1 text-xs font-medium rounded border ${theme.button} text-white border-transparent`}>Replace</button>
                        <button onClick={handleReplaceAll} className={`px-2 py-1 text-xs font-medium rounded border ${theme.border} hover:bg-gray-100 dark:hover:bg-gray-700`}>All</button>
                    </div>
                )}
            </div>
        )}

        {/* Editor Area */}
        <div className={`relative flex flex-1 overflow-hidden border rounded-lg ${!showSearch || hideSearch ? 'border-t' : 'border-t-0'} ${theme.border}`}>
            <div ref={linesRef} className={`flex-shrink-0 w-10 text-right pr-2 pt-4 select-none overflow-hidden text-xs font-mono border-r ${theme.border} ${theme.label} ${theme.panelBg}`} style={{ lineHeight: '1.5rem' }}>
            {Array.from({ length: Math.max(lineCount, 1) }, (_, i) => i + 1).map(l => <div key={l} className="opacity-50">{l}</div>)}
            </div>
            <textarea
            ref={textareaRef}
            value={value}
            onChange={onChange}
            onScroll={handleScroll}
            onKeyDown={handleKeyDown}
            readOnly={readOnly}
            placeholder={placeholder}
            className={`flex-1 w-full p-4 bg-transparent resize-none focus:outline-none font-mono text-sm ${theme.text} ${theme.editorBg}`}
            spellCheck="false"
            style={{ lineHeight: '1.5rem' }}
            />
        </div>
      </div>
    );
  }
);
LineNumberEditor.displayName = "LineNumberEditor";

// --- MAIN APP ---

export default function App() {
  
  // --- ROUTING LOGIC ---
  // Paths: '/json-to-binary' (Encoder) | '/binary-to-json' (Decoder) | '/privacy' | '/terms' | '/about'
  const getCurrentRoute = () => {
    if (typeof window !== 'undefined') {
        const path = window.location.pathname;
        if (path === '/privacy' || path === '/privacy/') return 'privacy';
        if (path === '/terms' || path === '/terms/') return 'terms';
        if (path === '/about' || path === '/about/') return 'about';
        if (path.includes('binary-to-json')) return 'decode';
    }
    return 'encode'; // Default to JSON -> Binary
  };

  const [currentRoute, setCurrentRoute] = useState<'encode' | 'decode' | 'privacy' | 'terms' | 'about'>(getCurrentRoute);
  const [view, setView] = useState<'encode' | 'decode'>(() => {
    const route = getCurrentRoute();
    return route === 'decode' ? 'decode' : 'encode';
  });

  const navigate = (mode: 'encode' | 'decode') => {
      const path = mode === 'encode' ? '/json-to-binary' : '/binary-to-json';
      window.history.pushState(null, '', path);
      setCurrentRoute(mode);
      setView(mode);
  };
  
  // Listen for back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      const route = getCurrentRoute();
      setCurrentRoute(route);
      if (route === 'encode' || route === 'decode') {
        setView(route);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // --- THEME & STATE ---
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('proto_dark_mode') !== 'false';
    }
    return true;
  });
  useEffect(() => localStorage.setItem('proto_dark_mode', String(darkMode)), [darkMode]);

  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      // On first load, keep sidebar open on medium+ screens, closed on small screens
      return window.innerWidth >= 768;
    }
    return true;
  });

  // Search Toggle State
  const [schemaSearchOpen, setSchemaSearchOpen] = useState(false);
  const [inputSearchOpen, setInputSearchOpen] = useState(false);
  const [outputSearchOpen, setOutputSearchOpen] = useState(false);

  const schemaEditorRef = useRef<LineNumberEditorHandle>(null);
  const inputEditorRef = useRef<LineNumberEditorHandle>(null);
  const outputEditorRef = useRef<LineNumberEditorHandle>(null);

  const defaultProto = `syntax = "proto3";

message Parent {
  string name = 1;
  int32 id = 2;
  string email = 3;
  bool is_active = 4;
}

message Child {
  string childname = 1;
  int32 id = 2;
  string email = 3;
  bool is_active = 4;
}`;

  // --- CHANGED: Added safe caching logic for Proto Input ---
  const [protoInput, setProtoInput] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('proto_def') || defaultProto;
    }
    return defaultProto;
  });
  
  useEffect(() => localStorage.setItem('proto_def', protoInput), [protoInput]);

  const [root, setRoot] = useState<protobuf.Root | null>(null);
  const [messageTypes, setMessageTypes] = useState<string[]>([]);
  const [selectedType, setSelectedType] = useState<string>('');
  const [protoError, setProtoError] = useState<string | null>(null);

  const [inputData, setInputData] = useState('');
  const [outputResult, setOutputResult] = useState('');
  const [dataError, setDataError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // --- THEME COLORS ---
  const theme = {
    bg: darkMode ? 'bg-[#1e1e1e]' : 'bg-gray-50',
    panelBg: darkMode ? 'bg-[#252526]' : 'bg-white',
    editorBg: darkMode ? 'bg-[#1e1e1e]' : 'bg-white',
    text: darkMode ? 'text-[#d4d4d4]' : 'text-gray-900',
    mutedText: darkMode ? 'text-gray-400' : 'text-gray-500',
    label: darkMode ? 'text-[#bbbbbb]' : 'text-gray-600',
    border: darkMode ? 'border-[#3e3e42]' : 'border-gray-200',
    inputBorder: darkMode ? 'border-[#3e3e42]' : 'border-gray-300',
    button: darkMode ? 'bg-[#0e639c] hover:bg-[#1177bb]' : 'bg-blue-600 hover:bg-blue-700',
    activeNav: darkMode ? 'bg-[#37373d] text-white' : 'bg-blue-100 text-blue-700',
    inactiveNav: darkMode ? 'text-[#cccccc] hover:bg-[#2a2d2e]' : 'text-gray-600 hover:bg-gray-100',
    adBg: darkMode ? 'bg-[#333333]' : 'bg-gray-200',
  };

  // --- LOGIC ---

  useEffect(() => {
    setInputData('');
    setOutputResult('');
    setDataError(null);
  }, [view]);

  useEffect(() => {
    try {
      const parsedRoot = protobuf.parse(protoInput).root;
      parsedRoot.resolveAll();
      const types: string[] = [];
      parsedRoot.nestedArray.forEach(nested => {
        if (nested instanceof protobuf.Type) types.push(nested.name);
      });
      setRoot(parsedRoot);
      setMessageTypes(types);
      setProtoError(null);
      if (types.length > 0 && (!selectedType || !types.includes(selectedType))) {
        setSelectedType(types[0]);
      }
    } catch (err: any) {
      setProtoError(err.message);
      setMessageTypes([]);
    }
  }, [protoInput, selectedType]);

  const handleGenerateTemplate = useCallback(() => {
      if (!root || !selectedType) return;
      try {
          const type = root.lookupType(selectedType);
          const template = generateTemplate(type);
          setInputData(JSON.stringify(template, null, 2));
          setDataError(null);
      } catch(e) { console.warn("Template gen failed", e); }
  }, [root, selectedType]);

  const handleLoadEmptyBinary = useCallback(() => {
      if (!root || !selectedType) return;
      try {
        const type = root.lookupType(selectedType);
        const template = generateTemplate(type);
        const message = type.create(template);
        const buffer = type.encode(message).finish();
        setInputData(bufferToHex(buffer));
        setDataError(null);
      } catch (e) {
        setDataError("Failed to generate binary template");
      }
  }, [root, selectedType]);

  useEffect(() => {
    if (view === 'encode' && root && selectedType && !inputData) {
        handleGenerateTemplate();
    }
  }, [selectedType, root, view, handleGenerateTemplate, inputData]);

  useEffect(() => {
    if (!root || !selectedType) {
      setOutputResult('');
      setDataError(null);
      return;
    }
    if (!inputData.trim()) {
        if(view === 'decode') { setOutputResult(''); return; }
        setOutputResult(''); return;
    }
    const timer = setTimeout(() => {
      try {
        const Type = root.lookupType(selectedType);
        setDataError(null);
        if (view === 'encode') {
          let payload;
          try { payload = JSON.parse(inputData); } catch(e) { throw new Error("Invalid JSON Syntax"); }
          const errMsg = Type.verify(payload);
          if (errMsg) throw Error(errMsg);
          const message = Type.create(payload);
          const buffer = Type.encode(message).finish();
          setOutputResult(bufferToHex(buffer));
        } else {
          let buffer;
          try {
             if (/^[0-9a-fA-F\s]+$/.test(inputData)) buffer = hexToBuffer(inputData);
             else buffer = Buffer.from(inputData, 'base64');
          } catch(e) { throw new Error("Invalid Binary Input (Hex or Base64)"); }
          const decoded = Type.decode(buffer);
          const object = Type.toObject(decoded, { longs: String, enums: String, bytes: String, defaults: true, arrays: true });
          setOutputResult(JSON.stringify(object, null, 2));
        }
      } catch (err: any) {
        setDataError(err.message);
        setOutputResult('');
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [inputData, root, selectedType, view]);

  const handleCopy = () => {
    navigator.clipboard.writeText(outputResult);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Render policy pages
  if (currentRoute === 'privacy') {
    return (
      <>
        <CookieConsent darkMode={darkMode} />
        <PrivacyPage darkMode={darkMode} />
      </>
    );
  }

  if (currentRoute === 'terms') {
    return (
      <>
        <CookieConsent darkMode={darkMode} />
        <TermsPage darkMode={darkMode} />
      </>
    );
  }

  if (currentRoute === 'about') {
    return (
      <>
        <CookieConsent darkMode={darkMode} />
        <AboutPage darkMode={darkMode} />
      </>
    );
  }

  return (
    <>
      <CookieConsent darkMode={darkMode} />
      <div className={`flex h-screen w-full font-sans overflow-hidden ${theme.bg} ${theme.text} transition-colors duration-200`}>
      
      {/* --- SIDEBAR --- */}
      <aside className={`${theme.panelBg} border-r ${theme.border} flex flex-col ${sidebarOpen ? 'w-64' : 'w-0'} transition-all duration-300 overflow-hidden relative z-20 flex-shrink-0`}>
        <div className="h-16 flex items-center gap-3 px-4 border-b border-gray-700/10 flex-shrink-0">
          <Database className="w-6 h-6 text-orange-500" />
          <span className={`text-lg font-bold whitespace-nowrap ${theme.text}`}>ProtoBench</span>
        </div>
        
        {/* MODIFIED: Ad moved inside nav, directly after buttons */}
        <nav className="p-4 space-y-2 flex-1 overflow-y-auto">
          <button onClick={() => navigate('encode')} className={`w-full flex items-center px-3 py-2 rounded-lg font-medium transition-colors ${view === 'encode' ? theme.activeNav : theme.inactiveNav}`}>
            <FileCode className="w-4 h-4 mr-2" />
            JSON to Binary
          </button>
          <button onClick={() => navigate('decode')} className={`w-full flex items-center px-3 py-2 rounded-lg font-medium transition-colors ${view === 'decode' ? theme.activeNav : theme.inactiveNav}`}>
            <ArrowRightLeft className="w-4 h-4 mr-2" />
            Binary to JSON
          </button>

          {/* Ad Placement: Added margin-top (mt-4) for spacing */}
          <div className={`mt-4 ${!sidebarOpen && 'hidden'}`}>
             <GoogleAd key="sidebar-ad" client={ADSENSE_CLIENT_ID} slot={ADSENSE_SLOT_SIDEBAR} format="auto" className={`rounded-lg overflow-hidden min-h-[250px] ${theme.adBg}`} />
          </div>
        </nav>

        {/* REMOVED: The bottom div containing the ad was deleted from here */}
      </aside>

      {/* --- RIGHT COLUMN --- */}
      <div className="flex-1 flex flex-col min-w-0 h-full overflow-y-auto">
        
        {/* --- HEADER --- */}
        <header className={`${theme.panelBg} border-b ${theme.border} h-16 flex items-center justify-between px-6 flex-shrink-0 sticky top-0 z-30 shadow-sm`}>
           <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(!sidebarOpen)} className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${theme.text}`}>
                <MenuIcon className="w-6 h-6" />
              </button>
              <h1 className="text-lg font-semibold hidden sm:block">
                {view === 'encode' ? 'Encoder (Compiler)' : 'Decoder (Parser)'}
              </h1>
           </div>
           
           <div className="flex items-center gap-4">
              <div className="flex items-center gap-2" title="Select the specific Message definition from your schema to use.">
                 <label className={`text-sm font-medium ${theme.label} hidden sm:block`}>Target Type:</label>
                 <div className="relative">
                   <select 
                     value={selectedType} 
                     onChange={(e) => setSelectedType(e.target.value)}
                     className={`appearance-none pl-3 pr-8 py-1.5 text-sm rounded border focus:ring-2 focus:ring-blue-500 outline-none ${theme.inputBorder} ${theme.panelBg} ${theme.text}`}
                   >
                     {messageTypes.length === 0 && <option>No types found</option>}
                     {messageTypes.map(t => <option key={t} value={t}>{t}</option>)}
                   </select>
                   <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none opacity-50" />
                 </div>
              </div>

              <div className={`h-6 w-px ${theme.border}`}></div>
              <button onClick={() => setDarkMode(!darkMode)} className={`p-2 rounded-lg ${theme.inactiveNav}`}>
                {darkMode ? <SunIcon className="w-5 h-5" /> : <MoonIcon className="w-5 h-5" />}
              </button>
           </div>
        </header>

        {/* --- WORKSPACE --- */}
        <main className="w-full max-w-full flex flex-col lg:flex-row gap-4 p-4 min-h-[85vh] flex-shrink-0">
          
          {/* LEFT: PROTO DEFINITION */}
          <div className={`flex flex-col lg:w-1/3 min-w-[300px] h-[500px] lg:h-auto rounded-lg border ${theme.border} ${theme.panelBg} overflow-hidden`}>
             <div className="px-4 py-2 border-b flex justify-between items-center bg-opacity-50 flex-shrink-0 h-10">
               <div className="flex items-center gap-2">
                    <span className="text-sm font-bold opacity-80">Schema (.proto)</span>
                    {protoError && <span className="text-xs text-red-500 flex items-center gap-1"><AlertTriangle className="w-3 h-3"/> Syntax Error</span>}
               </div>
               <button 
                  onClick={() => schemaEditorRef.current?.toggleSearch()}
                  className={`p-1.5 rounded-lg transition-colors flex items-center gap-1.5 text-xs font-medium ${schemaSearchOpen ? theme.activeNav : theme.inactiveNav}`}
                  title="Find & Replace (Ctrl+F)"
                >
                    <SearchIcon className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">{schemaSearchOpen ? 'Hide' : 'Search'}</span>
                </button>
             </div>
             <LineNumberEditor 
                ref={schemaEditorRef}
                value={protoInput} 
                onChange={(e: any) => setProtoInput(e.target.value)} 
                theme={theme} 
                className="border-none rounded-none"
                onSearchChange={setSchemaSearchOpen}
             />
          </div>

          {/* RIGHT: INPUT / OUTPUT */}
          <div className="flex-1 flex flex-col lg:flex-row gap-4 h-[600px] lg:h-auto">
            
            {/* INPUT PANEL */}
            <div className={`flex-1 flex flex-col rounded-lg border ${theme.border} ${theme.panelBg} overflow-hidden shadow-sm`}>
               <div className="px-3 py-2 border-b flex justify-between items-center flex-shrink-0 h-10 bg-opacity-50">
                 <span className="text-sm font-bold opacity-80">
                    {view === 'encode' ? 'Input JSON' : 'Input Binary'}
                 </span>
                 <div className="flex items-center gap-2">
                    {view === 'encode' && (
                        <button 
                            onClick={() => inputEditorRef.current?.toggleSearch()}
                            className={`p-1.5 rounded-lg transition-colors flex items-center gap-1.5 text-xs font-medium ${inputSearchOpen ? theme.activeNav : theme.inactiveNav}`}
                            title="Find & Replace (Ctrl+F)"
                        >
                            <SearchIcon className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">{inputSearchOpen ? 'Hide' : 'Search'}</span>
                        </button>
                    )}
                    <div className={`h-4 w-px ${theme.border}`}></div>
                    {view === 'encode' ? (
                       <IconButton 
                          onClick={handleGenerateTemplate} 
                          icon={<RefreshCw className="w-3 h-3"/>} 
                          label="Generate JSON"
                          tooltip="Generate dummy JSON based on schema"
                          theme={theme}
                       />
                    ) : (
                       <IconButton 
                          onClick={handleLoadEmptyBinary} 
                          icon={<FileDigit className="w-3 h-3"/>} 
                          label="Generate Hex" 
                          tooltip="Generate valid hex string for this message"
                          theme={theme}
                       />
                    )}
                    <div className={`h-4 w-px ${theme.border}`}></div>
                    <IconButton 
                       onClick={() => setInputData('')} 
                       icon={<TrashIcon className="w-3 h-3"/>} 
                       label="Clear" 
                       tooltip="Clear input"
                       theme={theme}
                    />
                 </div>
               </div>
               <LineNumberEditor 
                  ref={inputEditorRef}
                  value={inputData} 
                  onChange={(e: any) => setInputData(e.target.value)} 
                  theme={theme} 
                  className="border-none rounded-none"
                  placeholder={view === 'encode' ? "Generating template..." : "Paste Hex string (e.g. 0a 06 ...) or Base64"}
                  hideSearch={view === 'decode'}
                  onSearchChange={setInputSearchOpen}
               />
               <div className={`px-4 py-1.5 text-xs border-t ${theme.border} ${dataError ? 'bg-red-900/20 text-red-500' : 'text-gray-500'}`}>
                 {dataError || (view === 'encode' ? "Ready to encode" : "Waiting for input...")}
               </div>
            </div>

            {/* OUTPUT PANEL */}
            <div className={`flex-1 flex flex-col rounded-lg border ${theme.border} ${theme.panelBg} overflow-hidden shadow-sm`}>
               <div className="px-3 py-2 border-b flex justify-between items-center flex-shrink-0 h-10 bg-opacity-50">
                 <span className="text-sm font-bold opacity-80">
                    {view === 'encode' ? 'Binary Output' : 'JSON Output'}
                 </span>
                 <div className="flex items-center gap-2">
                     {view === 'decode' && (
                        <button 
                            onClick={() => outputEditorRef.current?.toggleSearch()}
                            className={`p-1.5 rounded-lg transition-colors flex items-center gap-1.5 text-xs font-medium ${outputSearchOpen ? theme.activeNav : theme.inactiveNav}`}
                            title="Find & Replace (Ctrl+F)"
                        >
                            <SearchIcon className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">{outputSearchOpen ? 'Hide' : 'Search'}</span>
                        </button>
                    )}
                     <div className={`h-4 w-px ${theme.border}`}></div>
                     <button 
                        onClick={handleCopy} 
                        className={`flex items-center gap-1.5 px-2 py-1 text-xs rounded border transition-colors ${copied ? 'bg-green-600 border-green-600 text-white' : `${theme.inactiveNav} border-transparent hover:border-gray-300 dark:hover:border-gray-600`}`}
                     >
                        {copied ? <CheckCircle className="w-3 h-3" /> : <CopyIcon className="w-3 h-3" />}
                        {copied ? 'Copied' : 'Copy Result'}
                     </button>
                 </div>
               </div>
               <LineNumberEditor 
                  ref={outputEditorRef}
                  value={outputResult} 
                  onChange={() => {}} 
                  readOnly={true}
                  theme={theme} 
                  className="border-none rounded-none"
                  placeholder="Result will appear here..."
                  hideSearch={view === 'encode'}
                  onSearchChange={setOutputSearchOpen}
               />
            </div>
          </div>
        </main>

        {/* --- SEO CONTENT --- */}
        <article className={`w-full max-w-4xl mx-auto px-6 prose ${darkMode ? 'prose-invert' : ''} ${theme.mutedText} text-sm mt-10 mb-12`}>
            {/* Intro Header */}
            <div className={`mb-8 border-b pb-8 ${theme.border}`}>
                <h2 className={`text-2xl font-bold mb-4 ${theme.text}`}>
                  {view === 'encode' ? 'Best Free Online Protobuf Compiler: JSON to Binary Converter' : 'Best Free Online Protobuf Decoder: Binary to JSON Parser'}
                </h2>
                <p className="mb-4 text-base leading-7">
                  Proto Workbench is the most advanced <strong>online Protobuf compiler</strong> and <strong>Protobuf decoder</strong> available. This free <strong>Protobuf visualizer</strong> allows you to 
                  <strong> {view === 'encode' ? 'compile JSON into Protobuf binary format' : 'decode Protobuf binary data back to JSON'}</strong> instantly within your browser. 
                  Whether you're working with <strong>gRPC APIs</strong>, debugging <strong>Protocol Buffers</strong>, analyzing binary payloads, or designing <code>.proto</code> schemas, 
                  this <strong>Protobuf compiler tool</strong> provides a robust, secure environment for all your Protobuf encoding and decoding needs.
                </p>
                <p className="mb-4 text-base leading-7">
                  As a <strong>client-side Protobuf compiler</strong>, all processing happens locally in your browser using <code>protobuf.js</code>. 
                  Your <code>.proto</code> schemas and data never leave your device, making this the safest <strong>online Protobuf tool</strong> for working with sensitive API schemas and production payloads.
                </p>
            </div>

            {/* What is Protobuf Section */}
            <div className={`mb-8 border-b pb-8 ${theme.border}`}>
                <h3 className={`text-xl font-semibold mb-4 ${theme.text}`}>What is Protocol Buffers (Protobuf)?</h3>
                <p className="mb-4 text-base leading-7">
                  <strong>Protocol Buffers</strong> (Protobuf) is a language-neutral, platform-neutral serialization format developed by Google. 
                  It's designed to be more efficient than JSON and XML, making it ideal for <strong>gRPC services</strong>, microservices communication, and high-performance APIs. 
                  Unlike JSON, Protobuf uses a binary format that's both smaller and faster to parse, which is why it's widely used in production systems.
                </p>
                <p className="mb-4 text-base leading-7">
                  A <strong>Protobuf compiler</strong> (also called a <strong>protoc compiler</strong>) converts human-readable <code>.proto</code> schema definitions into language-specific code. 
                  Our <strong>online Protobuf compiler</strong> goes a step further by allowing you to compile JSON data directly into Protobuf binary format, 
                  and decode Protobuf binary data back into readable JSON—all without installing any software.
                </p>
            </div>

            {/* Use Cases Section */}
            <div className={`mb-8 border-b pb-8 ${theme.border}`}>
                <h3 className={`text-xl font-semibold mb-4 ${theme.text}`}>Common Use Cases for Protobuf Compiler</h3>
                <div className="grid md:grid-cols-2 gap-6">
                    <div>
                        <h4 className={`font-semibold mb-2 ${theme.text}`}>gRPC API Development</h4>
                        <p className="text-sm leading-6">
                          Debug and test <strong>gRPC services</strong> by compiling JSON request payloads into Protobuf binary format. 
                          Use our <strong>Protobuf encoder</strong> to generate the exact binary data your gRPC endpoints expect.
                        </p>
                    </div>
                    <div>
                        <h4 className={`font-semibold mb-2 ${theme.text}`}>Binary Payload Analysis</h4>
                        <p className="text-sm leading-6">
                          Decode mysterious binary payloads from network traffic, logs, or databases. 
                          Our <strong>Protobuf decoder</strong> converts Hex or Base64 strings into readable JSON instantly.
                        </p>
                    </div>
                    <div>
                        <h4 className={`font-semibold mb-2 ${theme.text}`}>Schema Validation</h4>
                        <p className="text-sm leading-6">
                          Validate your <code>.proto</code> schema files before deploying to production. 
                          Get instant feedback on syntax errors, missing fields, and type mismatches.
                        </p>
                    </div>
                    <div>
                        <h4 className={`font-semibold mb-2 ${theme.text}`}>API Testing & Debugging</h4>
                        <p className="text-sm leading-6">
                          Test Protobuf-based APIs without writing code. Compile test data, decode responses, 
                          and verify payload structures using our <strong>Protobuf visualizer</strong>.
                        </p>
                    </div>
                </div>
            </div>

            {/* Features Grid */}
            <div className="grid md:grid-cols-2 gap-8 mb-10">
                <div>
                  <h3 className={`text-lg font-semibold mb-3 ${theme.text}`}>Advanced Protobuf Compiler Features</h3>
                  <ul className="list-disc pl-5 space-y-2 text-sm">
                    <li><strong>Real-Time Compilation:</strong> Our <strong>Protobuf compiler</strong> processes JSON to binary instantly as you type, with live validation and error detection.</li>
                    <li><strong>Smart Schema Editor:</strong> Full-featured editor with line numbers, <strong>Find & Replace</strong> (supports Regex), syntax highlighting, and syntax error detection.</li>
                    <li><strong>Auto-Template Generation:</strong> Automatically generate valid <strong>JSON templates</strong> or <strong>Hex strings</strong> based on your selected Message type definition—no manual typing required.</li>
                    <li><strong>Dual-Mode Operation:</strong> Seamlessly switch between <strong>Protobuf encoding</strong> (JSON to Binary) and <strong>Protobuf decoding</strong> (Binary to JSON) modes.</li>
                    <li><strong>Deep Linking:</strong> Dedicated URLs for <a href="/json-to-binary" className="underline text-blue-500">JSON to Binary</a> and <a href="/binary-to-json" className="underline text-blue-500">Binary to JSON</a> workflows.</li>
                    <li><strong>Nested Message Support:</strong> Handles complex nested <code>message</code> types, <code>repeated</code> fields (arrays), and <code>map</code> fields automatically.</li>
                  </ul>
                </div>
                <div>
                  <h3 className={`text-lg font-semibold mb-3 ${theme.text}`}>Privacy, Security & Performance</h3>
                  <ul className="list-disc pl-5 space-y-2 text-sm">
                    <li><strong>100% Client-Side Processing:</strong> Powered by <code>protobuf.js</code>, all <strong>Protobuf compilation</strong> happens locally. Your schemas and data never leave your browser—perfect for sensitive production data.</li>
                    <li><strong>Multiple Format Support:</strong> Flexible input handling for <strong>Hexadecimal</strong> (spaced like <code>0a 06 74 65 73 74</code> or continuous), <strong>Base64</strong> encoded binaries, and standard JSON.</li>
                    <li><strong>Real-Time Validation:</strong> Instant syntax error checking for both your <code>.proto</code> schema definitions and your JSON/binary data inputs.</li>
                    <li><strong>No Installation Required:</strong> This <strong>online Protobuf compiler</strong> works entirely in your browser—no downloads, no server uploads, no account registration.</li>
                    <li><strong>Dark & Light Themes:</strong> Comfortable editing experience with both dark and light mode support.</li>
                    <li><strong>Offline Capable:</strong> After initial page load, the <strong>Protobuf compiler</strong> works entirely offline—perfect for working in restricted network environments.</li>
                  </ul>
                </div>
            </div>

            {/* How It Works Section */}
            <div className={`mb-8 border-b pb-8 ${theme.border}`}>
                <h3 className={`text-xl font-semibold mb-4 ${theme.text}`}>How to Use This Protobuf Compiler</h3>
                <div className="space-y-4">
                    <div>
                        <h4 className={`font-semibold mb-2 ${theme.text}`}>Step 1: Define Your Schema</h4>
                        <p className="text-sm leading-6">
                          Paste or write your <code>.proto</code> schema definition in the Schema Editor. Our <strong>Protobuf compiler</strong> will automatically parse and validate the syntax, 
                          extracting all available <code>message</code> types for you to use.
                        </p>
                    </div>
                    <div>
                        <h4 className={`font-semibold mb-2 ${theme.text}`}>Step 2: Select Message Type</h4>
                        <p className="text-sm leading-6">
                          Choose the target <code>message</code> type from the dropdown in the header. This tells the <strong>Protobuf encoder</strong> which schema definition to use for compilation.
                        </p>
                    </div>
                    <div>
                        <h4 className={`font-semibold mb-2 ${theme.text}`}>
                          {view === 'encode' ? 'Step 3: Compile JSON to Protobuf Binary' : 'Step 3: Decode Protobuf Binary to JSON'}
                        </h4>
                        <p className="text-sm leading-6">
                          {view === 'encode' 
                            ? 'Enter your JSON data in the Input panel. The Protobuf compiler will automatically validate the data against your schema and generate the binary output in Hexadecimal format. Copy the result or download it for use in your applications.'
                            : 'Paste your Protobuf binary data as a Hex string (e.g., "0a 06 74 65 73 74") or Base64 string in the Input panel. Our Protobuf decoder will parse the binary and convert it back to readable JSON format.'}
                        </p>
                    </div>
                </div>
            </div>

            {/* Protobuf vs JSON Section */}
            <div className={`mb-8 border-b pb-8 ${theme.border}`}>
                <h3 className={`text-xl font-semibold mb-4 ${theme.text}`}>Protobuf vs JSON: Why Use a Protobuf Compiler?</h3>
                <div className="grid md:grid-cols-2 gap-6">
                    <div>
                        <h4 className={`font-semibold mb-2 ${theme.text}`}>Protobuf Advantages</h4>
                        <ul className="list-disc pl-5 space-y-1 text-sm">
                            <li><strong>Smaller Size:</strong> Binary format is 3-10x smaller than JSON</li>
                            <li><strong>Faster Parsing:</strong> Binary parsing is significantly faster</li>
                            <li><strong>Type Safety:</strong> Strong typing with schema validation</li>
                            <li><strong>Backward Compatible:</strong> Schema evolution without breaking changes</li>
                            <li><strong>Language Agnostic:</strong> Works with any programming language</li>
                        </ul>
                    </div>
                    <div>
                        <h4 className={`font-semibold mb-2 ${theme.text}`}>When to Use JSON</h4>
                        <ul className="list-disc pl-5 space-y-1 text-sm">
                            <li>Human-readable debugging and logging</li>
                            <li>Simple APIs that don't need high performance</li>
                            <li>Web frontend communication</li>
                            <li>Configuration files</li>
                            <li>When schema flexibility is more important than performance</li>
                        </ul>
                    </div>
                </div>
                <p className="mt-4 text-sm leading-6">
                  Our <strong>Protobuf compiler</strong> bridges both worlds—compile JSON to Protobuf for production efficiency, 
                  decode Protobuf to JSON for debugging and readability. This makes it the perfect tool for developers working with both formats.
                </p>
            </div>

            {/* FAQ */}
            <h3 className={`text-xl font-semibold mt-8 mb-6 ${theme.text}`}>Frequently Asked Questions About Protobuf Compiler</h3>
            <div className="space-y-6">
                <div>
                    <h4 className={`font-medium mb-2 ${theme.text}`}>Is this a free Protobuf compiler?</h4>
                    <p className="text-sm leading-6">
                      Yes! Proto Workbench is completely free to use. There are no registration requirements, no usage limits, and no hidden fees. 
                      As a <strong>client-side Protobuf compiler</strong>, all processing happens in your browser, so we don't even need to store your data.
                    </p>
                </div>
                <div>
                    <h4 className={`font-medium mb-2 ${theme.text}`}>How do I compile JSON to Protobuf binary?</h4>
                    <p className="text-sm leading-6">
                      Simply paste your <code>.proto</code> schema in the Schema Editor, select your target message type, and enter your JSON data in the Input panel. 
                      Our <strong>Protobuf compiler</strong> will automatically validate and compile your JSON into Protobuf binary format, displayed as a Hexadecimal string. 
                      You can then copy this binary data for use in your gRPC services or other Protobuf-based applications.
                    </p>
                </div>
                <div>
                    <h4 className={`font-medium mb-2 ${theme.text}`}>Can I decode Protobuf binary back to JSON?</h4>
                    <p className="text-sm leading-6">
                      Absolutely! Switch to the "Binary to JSON" mode (or visit <a href="/binary-to-json" className="underline text-blue-500">/binary-to-json</a>), 
                      paste your Protobuf binary data as a Hex string or Base64 string, and our <strong>Protobuf decoder</strong> will convert it back to readable JSON format. 
                      This is perfect for debugging binary payloads from network traffic or analyzing Protobuf-encoded data.
                    </p>
                </div>
                <div>
                    <h4 className={`font-medium mb-2 ${theme.text}`}>Does this Protobuf compiler support proto3?</h4>
                    <p className="text-sm leading-6">
                      Yes! Our <strong>online Protobuf compiler</strong> fully supports <strong>proto3</strong> syntax, which is the latest version of Protocol Buffers. 
                      It handles all proto3 features including optional fields, map types, oneof, and well-known types.
                    </p>
                </div>
                <div>
                    <h4 className={`font-medium mb-2 ${theme.text}`}>Is my data secure with this online Protobuf compiler?</h4>
                    <p className="text-sm leading-6">
                      Yes, your data is completely secure. Unlike server-based <strong>Protobuf compilers</strong>, Proto Workbench processes everything locally in your browser using <code>protobuf.js</code>. 
                      Your <code>.proto</code> schemas, JSON data, and binary payloads never leave your device. This makes it safe for working with sensitive production data, API keys, and internal schemas.
                    </p>
                </div>
                <div>
                    <h4 className={`font-medium mb-2 ${theme.text}`}>How do I use the Find & Replace feature?</h4>
                    <p className="text-sm leading-6">
                      Press <code>Ctrl+F</code> (or <code>Cmd+F</code> on Mac) inside any editor pane to open the search bar. You can search for field names, values, or schema definitions. 
                      Press <code>Enter</code> to cycle through matches. The Find & Replace feature supports regular expressions for advanced search patterns.
                    </p>
                </div>
                <div>
                    <h4 className={`font-medium mb-2 ${theme.text}`}>Why does the template get auto-generated?</h4>
                    <p className="text-sm leading-6">
                      When you select a <code>Message</code> type from the dropdown, Proto Workbench automatically scans your schema and builds a valid JSON object (or binary equivalent) with default values. 
                      This saves you from manually typing out boilerplate JSON and ensures your data structure matches the schema exactly.
                    </p>
                </div>
                <div>
                    <h4 className={`font-medium mb-2 ${theme.text}`}>Does this Protobuf compiler support nested messages?</h4>
                    <p className="text-sm leading-6">
                      Yes! The recursive generator handles nested <code>message</code> types, <code>repeated</code> fields (arrays), and <code>map</code> fields automatically. 
                      You can work with complex, deeply nested Protobuf schemas without any limitations.
                    </p>
                </div>
                <div>
                    <h4 className={`font-medium mb-2 ${theme.text}`}>Can I use this Protobuf compiler offline?</h4>
                    <p className="text-sm leading-6">
                      After the initial page load, yes! All the <strong>Protobuf compilation</strong> logic runs entirely in your browser, so you can use this tool offline. 
                      Your browser will cache the necessary JavaScript libraries, allowing you to compile and decode Protobuf data even without an internet connection.
                    </p>
                </div>
                <div>
                    <h4 className={`font-medium mb-2 ${theme.text}`}>What formats does the Protobuf decoder support?</h4>
                    <p className="text-sm leading-6">
                      Our <strong>Protobuf decoder</strong> accepts binary data in two formats: <strong>Hexadecimal</strong> (both spaced like <code>0a 06 74 65 73 74</code> and continuous like <code>0a0674657374</code>) 
                      and <strong>Base64</strong> encoded strings. This flexibility makes it easy to decode Protobuf data from various sources including network packets, logs, and databases.
                    </p>
                </div>
            </div>

            {/* Related Tools Section */}
            <div className={`mt-8 pt-6 border-t ${theme.border}`}>
                <h3 className={`text-lg font-semibold mb-4 ${theme.text}`}>Related Developer Tools</h3>
                <p className="text-sm leading-6 mb-4">
                  If you're working with JSON data, check out our <a href="https://jsonformatter.echologai.com" target="_blank" rel="noopener noreferrer" className="underline text-blue-500">JSON Formatter Pro</a> for formatting, 
                  validating, and minifying JSON. For more developer tools from the EchoLog ecosystem, visit <a href="https://echologai.com" target="_blank" rel="noopener noreferrer" className="underline text-blue-500">Echolog AI</a>.
                </p>
            </div>
        </article>

        {/* --- Footer & AD: Horizontal --- */}
        <footer className={`flex-shrink-0 pt-0 flex flex-col items-center gap-4 border-t ${theme.border} ${theme.bg}`}>
          <div className="w-full max-w-4xl px-4 pt-6">
            <GoogleAd 
              key="footer-ad"
              client={ADSENSE_CLIENT_ID}
              slot={ADSENSE_SLOT_FOOTER}
              format="horizontal"
              className={`flex items-center justify-center w-full min-h-[90px] rounded-lg overflow-hidden ${theme.adBg}`}
            />
          </div>
          <Footer darkMode={darkMode} />
        </footer>

      </div>
    </div>
    </>
  );
}